import moment, { Moment } from 'moment';
import React, { useMemo, useState } from 'react';
import { Alert, DatePicker, Empty, Spin, Table } from 'antd';
import { LineChart, XAxis, YAxis, Line, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { useQuery } from 'react-query';
import styled from 'styled-components';

import { ApiClient } from '../../api';
import { DrawerLayout } from '../../Layout';
import { convertNumberToCommaString } from '../../utils/helper';
import styles from './styles';

const InsightsWrapper = styled.div`
    ${styles}
`;

const { RangePicker } = DatePicker;

const palette = ['#1E87F0', '#FF8743', '#2CCC71', '#9B51E0', '#F2994A', '#00A6A6', '#EB5757', '#07004D'];

interface BreakdownItem {
    platform?: string;
    type?: string;
    count?: number | string | null;
}

interface DailyStatsDay {
    date?: string;
    subscribed?: {
        total?: number | string | null;
        platforms?: BreakdownItem[];
        types?: BreakdownItem[];
    };
    unsubscribed?: {
        total?: number | string | null;
        platforms?: BreakdownItem[];
        types?: BreakdownItem[];
    };
}

interface TypeSummary {
    type?: string;
    subscribed?: number | string | null;
    unsubscribed?: number | string | null;
    active?: number | string | null;
}

interface DailyStatsResponse {
    days?: DailyStatsDay[];
    totalSubscribed?: number | string | null;
    totalUnsubscribed?: number | string | null;
    allTimeSubscribed?: number | string | null;
    allTimeUnsubscribed?: number | string | null;
    activeSubscriptions?: number | string | null;
    byType?: TypeSummary[];
}

interface SeriesConfig {
    dataKey: string;
    name: string;
    stroke: string;
}

interface NormalizedStats {
    rows: any[];
    summaryCards: { label: string; value: number; percentage: number }[];
    totalSeries: SeriesConfig[];
    subscribedPlatformSeries: SeriesConfig[];
    unsubscribedPlatformSeries: SeriesConfig[];
    subscribedTypeSeries: SeriesConfig[];
    unsubscribedTypeSeries: SeriesConfig[];
    typeRows: { type: string; subscribed: number; unsubscribed: number; active: number }[];
}

const safeNumber = (value: any) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
};

const formatNumber = (value: any) => convertNumberToCommaString(safeNumber(value));

const getPercentage = (value: number, total: number) => {
    if (total <= 0) {
        return 0;
    }
    return (safeNumber(value) / safeNumber(total)) * 100;
};

const formatPercentage = (value: any) => {
    const percentage = safeNumber(value);
    const rounded = Math.round(percentage * 10) / 10;
    return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
};

const formatName = (value?: string) => {
    const name = value || 'untracked';
    return name
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getName = (value?: string | null) => {
    const name = String(value || '').trim();
    return name || 'untracked';
};

const getSlug = (value?: string | null) => {
    return getName(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
};

const getSeriesKey = (status: 'subscribed' | 'unsubscribed', dimension: 'platform' | 'type', name?: string | null) => {
    return `${status}_${dimension}_${getSlug(name)}`;
};

const getRangeDays = (from: string, to: string) => {
    const start = moment(from, 'YYYY-MM-DD', true);
    const end = moment(to, 'YYYY-MM-DD', true);
    if (!start.isValid() || !end.isValid()) {
        return [];
    }

    const safeStart = start.isAfter(end) ? end.clone() : start.clone();
    const safeEnd = start.isAfter(end) ? start.clone() : end.clone();
    const days: string[] = [];
    const cursor = safeStart.clone();
    while (cursor.isSameOrBefore(safeEnd, 'day')) {
        days.push(cursor.format('YYYY-MM-DD'));
        cursor.add(1, 'day');
    }
    return days;
};

const addValue = (row: any, key: string, value: any) => {
    row[key] = safeNumber(row[key]) + safeNumber(value);
};

const addBreakdown = (
    row: any,
    section: DailyStatsDay['subscribed'],
    status: 'subscribed' | 'unsubscribed',
    dimension: 'platform' | 'type',
    names: Set<string>,
) => {
    const list = dimension === 'platform' ? section?.platforms : section?.types;
    const safeList = Array.isArray(list) ? list : [];

    safeList.forEach((item) => {
        const name = getName(dimension === 'platform' ? item?.platform : item?.type);
        names.add(name);
        addValue(row, getSeriesKey(status, dimension, name), item?.count);
    });
};

const buildSeries = (status: 'subscribed' | 'unsubscribed', dimension: 'platform' | 'type', names: Set<string>) => {
    return Array.from(names)
        .sort((a, b) => formatName(a).localeCompare(formatName(b)))
        .map((name, index) => ({
            dataKey: getSeriesKey(status, dimension, name),
            name: formatName(name),
            stroke: palette[index % palette.length],
        }));
};

const ensureSeriesValues = (rows: any[], series: SeriesConfig[]) => {
    rows.forEach((row) => {
        series.forEach((item) => {
            if (typeof row[item.dataKey] === 'undefined') {
                row[item.dataKey] = 0;
            }
        });
    });
};

const getChartMax = (rows: any[], series: SeriesConfig[]) => {
    const maxValue = rows.reduce((max, row) => {
        const rowMax = series.reduce((currentMax, item) => Math.max(currentMax, safeNumber(row[item.dataKey])), 0);
        return Math.max(max, rowMax);
    }, 0);
    return maxValue > 0 ? Math.ceil(maxValue * 1.1) : 1;
};

const buildFallbackTypeRows = (rows: any[], typeSeries: SeriesConfig[]) => {
    return typeSeries.map((series) => {
        const slug = series.dataKey.replace('subscribed_type_', '');
        const subscribedKey = `subscribed_type_${slug}`;
        const unsubscribedKey = `unsubscribed_type_${slug}`;
        const subscribed = rows.reduce((sum, row) => sum + safeNumber(row[subscribedKey]), 0);
        const unsubscribed = rows.reduce((sum, row) => sum + safeNumber(row[unsubscribedKey]), 0);
        return {
            type: series.name,
            subscribed,
            unsubscribed,
            active: Math.max(subscribed - unsubscribed, 0),
        };
    });
};

const normalizeStats = (stats: DailyStatsResponse | undefined, from: string, to: string): NormalizedStats => {
    const days = Array.isArray(stats?.days) ? stats?.days || [] : [];
    const subscribedPlatforms = new Set<string>();
    const unsubscribedPlatforms = new Set<string>();
    const subscribedTypes = new Set<string>();
    const unsubscribedTypes = new Set<string>();
    const byDate = new Map<string, DailyStatsDay>();

    days.forEach((day) => {
        if (day?.date && moment(day.date, 'YYYY-MM-DD', true).isValid()) {
            byDate.set(day.date, day);
        }
    });

    const rows = getRangeDays(from, to).map((date) => {
        const day = byDate.get(date);
        const row: any = {
            date,
            subscribedTotal: safeNumber(day?.subscribed?.total),
            unsubscribedTotal: safeNumber(day?.unsubscribed?.total),
        };

        addBreakdown(row, day?.subscribed, 'subscribed', 'platform', subscribedPlatforms);
        addBreakdown(row, day?.unsubscribed, 'unsubscribed', 'platform', unsubscribedPlatforms);
        addBreakdown(row, day?.subscribed, 'subscribed', 'type', subscribedTypes);
        addBreakdown(row, day?.unsubscribed, 'unsubscribed', 'type', unsubscribedTypes);

        return row;
    });

    const totalSeries = [
        { dataKey: 'subscribedTotal', name: 'Subscribed', stroke: '#1E87F0' },
        { dataKey: 'unsubscribedTotal', name: 'Unsubscribed', stroke: '#FF8743' },
    ];
    const subscribedPlatformSeries = buildSeries('subscribed', 'platform', subscribedPlatforms);
    const unsubscribedPlatformSeries = buildSeries('unsubscribed', 'platform', unsubscribedPlatforms);
    const subscribedTypeSeries = buildSeries('subscribed', 'type', subscribedTypes);
    const unsubscribedTypeSeries = buildSeries('unsubscribed', 'type', unsubscribedTypes);
    const allSeries = [
        ...totalSeries,
        ...subscribedPlatformSeries,
        ...unsubscribedPlatformSeries,
        ...subscribedTypeSeries,
        ...unsubscribedTypeSeries,
    ];

    ensureSeriesValues(rows, allSeries);

    const selectedSubscribed = rows.reduce((sum, row) => sum + safeNumber(row.subscribedTotal), 0);
    const selectedUnsubscribed = rows.reduce((sum, row) => sum + safeNumber(row.unsubscribedTotal), 0);
    const totalSubscribed = stats?.totalSubscribed == null ? selectedSubscribed : safeNumber(stats.totalSubscribed);
    const totalUnsubscribed =
        stats?.totalUnsubscribed == null ? selectedUnsubscribed : safeNumber(stats.totalUnsubscribed);
    const allTimeSubscribed = stats?.allTimeSubscribed == null ? totalSubscribed : safeNumber(stats.allTimeSubscribed);
    const allTimeUnsubscribed =
        stats?.allTimeUnsubscribed == null ? totalUnsubscribed : safeNumber(stats.allTimeUnsubscribed);
    const activeSubscriptions =
        stats?.activeSubscriptions == null
            ? Math.max(allTimeSubscribed - allTimeUnsubscribed, 0)
            : safeNumber(stats.activeSubscriptions);
    const rangeTotal = totalSubscribed + totalUnsubscribed;
    const allTimeTotal = allTimeSubscribed + allTimeUnsubscribed;

    const typeRows = Array.isArray(stats?.byType)
        ? (stats?.byType || []).map((item) => ({
              type: formatName(getName(item?.type)),
              subscribed: safeNumber(item?.subscribed),
              unsubscribed: safeNumber(item?.unsubscribed),
              active: safeNumber(item?.active),
          }))
        : buildFallbackTypeRows(rows, subscribedTypeSeries);

    return {
        rows,
        summaryCards: [
            {
                label: 'Successful Subscriptions',
                value: totalSubscribed,
                percentage: getPercentage(totalSubscribed, rangeTotal),
            },
            {
                label: 'Selected Range Drop-Offs',
                value: totalUnsubscribed,
                percentage: getPercentage(totalUnsubscribed, rangeTotal),
            },
            {
                label: 'Active subscriptions',
                value: activeSubscriptions,
                percentage: getPercentage(activeSubscriptions, allTimeSubscribed),
            },
            {
                label: 'All time subscribed',
                value: allTimeSubscribed,
                percentage: getPercentage(allTimeSubscribed, allTimeTotal),
            },
            {
                label: 'Funnel Drop-Offs',
                value: allTimeUnsubscribed,
                percentage: getPercentage(allTimeUnsubscribed, allTimeTotal),
            },
        ],
        totalSeries,
        subscribedPlatformSeries,
        unsubscribedPlatformSeries,
        subscribedTypeSeries,
        unsubscribedTypeSeries,
        typeRows,
    };
};

const ChartCard = ({ title, rows, series }: { title: string; rows: any[]; series: SeriesConfig[] }) => {
    const [opacity, setOpacity] = useState<any>({});
    const maxValue = useMemo(() => getChartMax(rows, series), [rows, series]);

    const handleMouseEnter = (item: any) => {
        if (item?.dataKey) {
            setOpacity((state) => ({ ...state, [item.dataKey]: 0.45 }));
        }
    };

    const handleMouseLeave = (item: any) => {
        if (item?.dataKey) {
            setOpacity((state) => ({ ...state, [item.dataKey]: 1 }));
        }
    };

    return (
        <div className="chart-card">
            <div className="chart-card-header">{title}</div>
            {series.length === 0 ? (
                <div className="empty-content">
                    <Empty description="No data" />
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="#f0f0f0" vertical={false} />
                        <Legend verticalAlign="top" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
                        <YAxis allowDecimals={false} tickCount={5} domain={[0, maxValue]} />
                        <XAxis
                            dataKey="date"
                            minTickGap={20}
                            tickFormatter={(value) => moment(value).format('DD MMM')}
                        />
                        <Tooltip
                            labelFormatter={(label) => moment(label).format('DD MMM YYYY')}
                            formatter={(value: any, name: any) => [formatNumber(value), name]}
                        />
                        {series.map((item) => (
                            <Line
                                key={item.dataKey}
                                name={item.name}
                                dot={false}
                                type="monotone"
                                dataKey={item.dataKey}
                                stroke={item.stroke}
                                strokeWidth={2}
                                opacity={opacity[item.dataKey] ?? 1}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

const typeColumns = [
    {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
    },
    {
        title: 'Subscribed',
        dataIndex: 'subscribed',
        key: 'subscribed',
        render: formatNumber,
    },
    {
        title: 'Unsubscribed',
        dataIndex: 'unsubscribed',
        key: 'unsubscribed',
        render: formatNumber,
    },
    {
        title: 'Active',
        dataIndex: 'active',
        key: 'active',
        render: formatNumber,
    },
];

const getDefaultDateRange = (): [Moment, Moment] => [moment().subtract(29, 'days'), moment()];

const InsightsComponent = () => {
    const [dateRange, setDateRange] = useState<[Moment, Moment]>(getDefaultDateRange);
    const from = dateRange[0].format('YYYY-MM-DD');
    const to = dateRange[1].format('YYYY-MM-DD');
    const { data, isError, isFetching } = useQuery<DailyStatsResponse>(
        ['subscriptionDailyStats', from, to],
        ApiClient.getSubscriptionDailyStats,
        {
            refetchOnWindowFocus: false,
        },
    );
    const normalizedStats = useMemo(() => normalizeStats(data, from, to), [data, from, to]);

    return (
        <DrawerLayout>
            <InsightsWrapper>
                <div className="insights-toolbar">
                    <div className="insights-title">Subscription Insights</div>
                    <RangePicker
                        allowClear={false}
                        value={dateRange}
                        onChange={(values) => {
                            if (values && values[0] && values[1]) {
                                setDateRange([values[0], values[1]]);
                            }
                        }}
                    />
                </div>

                {isError ? (
                    <Alert
                        className="insights-error"
                        type="error"
                        showIcon
                        message="Unable to load subscription insights"
                    />
                ) : null}

                {isFetching && !data ? (
                    <div className="loading-content">
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        <div className="summary-grid">
                            {normalizedStats.summaryCards.map((item) => (
                                <div className="summary-card" key={item.label}>
                                    <div className="summary-card-label">{item.label}</div>
                                    <div className="summary-card-value">
                                        {formatNumber(item.value)}
                                        <span className="summary-card-percentage">
                                            ({formatPercentage(item.percentage)})
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="chart-grid">
                            <ChartCard
                                title="Daily Subscription Movement"
                                rows={normalizedStats.rows}
                                series={normalizedStats.totalSeries}
                            />
                            <ChartCard
                                title="Subscribed by Platform"
                                rows={normalizedStats.rows}
                                series={normalizedStats.subscribedPlatformSeries}
                            />
                            <ChartCard
                                title="Unsubscribed by Platform"
                                rows={normalizedStats.rows}
                                series={normalizedStats.unsubscribedPlatformSeries}
                            />
                            <ChartCard
                                title="Subscribed by Type"
                                rows={normalizedStats.rows}
                                series={normalizedStats.subscribedTypeSeries}
                            />
                            <ChartCard
                                title="Unsubscribed by Type"
                                rows={normalizedStats.rows}
                                series={normalizedStats.unsubscribedTypeSeries}
                            />
                        </div>

                        <div className="type-card">
                            <div className="type-card-header">Subscription Type Summary</div>
                            <Table
                                rowKey="type"
                                columns={typeColumns}
                                dataSource={normalizedStats.typeRows}
                                pagination={false}
                                locale={{ emptyText: 'No data' }}
                                size="small"
                                scroll={{ x: true }}
                            />
                        </div>
                    </>
                )}
            </InsightsWrapper>
        </DrawerLayout>
    );
};

export default InsightsComponent;
